import { Status } from '@common/enums/global.enums'
import {
	CertificationFormValues,
	ExpiringCertification,
	TraineeCertification
} from '@common/types/certification.types'
import {
	fetchFile,
	get,
	patch,
	post
} from '@common/utils/request.utils'

type CertificationApiRequests = {
	getExpiringCertifications: () => Promise<ExpiringCertification[]>
	getTraineeCertifications: (traineeId: string) => Promise<TraineeCertification[]>
	createCertification: (traineeId: string, values: CertificationFormValues) => Promise<TraineeCertification>
	updateCertification: (certificationId: string, values: CertificationFormValues) => Promise<TraineeCertification>
	updateExpiryNotification: (id: string, value: boolean) => Promise<ExpiringCertification>
	deleteCertification: (id: string) => Promise<TraineeCertification>
	downloadCertification: (certificationId: string) => Promise<void>
	exportCertifications: () => Promise<void>
}

const CERTIFICATION_API_URL = '/certifications'

/**
 * Certification api requests
 */
export const useCertificationApi = (): CertificationApiRequests => {
	const getExpiringCertifications = (): Promise<ExpiringCertification[]> => get(`${CERTIFICATION_API_URL}/expiring`)

	const getTraineeCertifications = (traineeId: string): Promise<TraineeCertification[]> => {
		return get(`${CERTIFICATION_API_URL}/persons/${traineeId}`)
	}

	const createCertification = (traineeId: string, values: CertificationFormValues): Promise<TraineeCertification> => {
		const { obtaining: { isTrainer }, ...other } = values
		return post(
			`${CERTIFICATION_API_URL}`,
			JSON.stringify({ ...other, isTrainer, traineeId })
		)
	}

	const updateCertification = (certificationId: string, values: CertificationFormValues): Promise<TraineeCertification> => {
		const { obtaining: { isTrainer }, ...other } = values
		return patch(
			`${CERTIFICATION_API_URL}/${certificationId}`,
			JSON.stringify({ ...other, isTrainer })
		)
	}

	const updateExpiryNotification = (id: string, value: boolean): Promise<ExpiringCertification> => {
		return patch(
			`${CERTIFICATION_API_URL}/${id}/expiryNotification`,
			JSON.stringify({ value })
		)
	}

	const updateStatus = (id: string, status: Status): Promise<TraineeCertification> => {
		return patch(`${CERTIFICATION_API_URL}/${id}/status`, JSON.stringify({ status }))
	}

	const deleteCertification = (id: string): Promise<TraineeCertification> => {
		return updateStatus(id, Status.DELETED)
	}

	const downloadCertification = (certificationId: string): Promise<void> => {
		return fetchFile(`${CERTIFICATION_API_URL}/${certificationId}/download`)
	}

	const exportCertifications = (): Promise<void> => {
		return fetchFile(`${CERTIFICATION_API_URL}/export`)
	}

	return {
		getExpiringCertifications,
		getTraineeCertifications,
		createCertification,
		updateCertification,
		updateExpiryNotification,
		deleteCertification,
		downloadCertification,
		exportCertifications
	}
}
